import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Share2, RotateCcw, Loader2, AlertTriangle, Sparkles, Building2, BookmarkCheck, Wrench, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, API, saveScanId, addSavedKey, getSavedKeys } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HealthGauge } from "@/components/report/HealthGauge";
import {
  SectionTitle, CategoryScores, TopOpportunities, FindingsSection, ReputationCard,
  OutdatedInfoCard, MenuServicesCard, CompetitorSection, PotentialOpportunity, LeadGenCTA,
  ExecutiveSummary, NextSteps, FixPlanSummary, ProvenanceLegend,
} from "@/components/report/ReportSections";
import { LeadForm } from "@/components/report/LeadForm";
import { Battlecard } from "@/components/report/Battlecard";
import { BusinessIntelDashboard } from "@/components/report/BiSections";
import SocialIntelligence from "@/components/report/SocialIntelligence";
import OwnerConcerns from "@/components/report/OwnerConcerns";
import IndustrySources from "@/components/report/IndustrySources";
import ReputationIntelligence from "@/components/report/ReputationIntelligence";
import { FinancialImpact } from "@/components/report/FinancialImpact";
import { ComplianceSection, ComplianceScoreBadge, ImpactOverview } from "@/components/report/ComplianceSection";
import { BusinessProfileCard } from "@/components/report/BusinessProfileCard";

export default function Report() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user;
  const [scan, setScan] = useState(null);
  const [status, setStatus] = useState("loading");
  const [leadPrefill, setLeadPrefill] = useState({ interest: "Full business audit", message: "" });
  const [added, setAdded] = useState({});
  const [fixStats, setFixStats] = useState(null);
  const [finVersion, setFinVersion] = useState(null);
  const [savingBiz, setSavingBiz] = useState(false);
  const [bizSaved, setBizSaved] = useState(false);

  const saveToMyBusinesses = async () => {
    const profile = scan?.report?.business_profile;
    if (!profile || savingBiz) return;
    setSavingBiz(true);
    try {
      const { data } = await api.post("/saved-businesses", { profile });
      addSavedKey(data.business_key);
      setBizSaved(true);
      toast.success("Saved to My Businesses", { action: { label: "View", onClick: () => nav("/businesses") } });
    } catch (e) {
      toast.error("Could not save this business.");
    } finally { setSavingBiz(false); }
  };

  const recomputeFinancial = async (opts) => {
    try {
      const { data } = await api.post(`/scan/${id}/financial/recompute`, opts || {});
      setScan((s) => ({ ...s, report: { ...s.report, financial: data.financial } }));
      setFinVersion(data.version);
      toast.success("Financial model recomputed (new version saved)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not recompute. Admin login required.");
      throw e;
    }
  };
  const overrideFinancial = async (payload) => {
    try {
      const { data } = await api.post(`/scan/${id}/financial/override`, payload);
      setScan((s) => ({ ...s, report: { ...s.report, financial: data.financial } }));
      setFinVersion(data.version);
      toast.success(payload.action === "clear" ? "Override cleared" : "Admin override applied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
      throw e;
    }
  };
  const overrideCompliance = async (payload) => {
    try {
      const { data } = await api.post(`/scan/${id}/compliance/override`, payload);
      setScan((s) => ({ ...s, report: { ...s.report, compliance: data.compliance } }));
      toast.success(payload.action === "clear" ? "Confirmation cleared" : "License status confirmed (Admin)");
      refreshFixStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Admin login required.");
      throw e;
    }
  };

  const scrollToLead = () => document.getElementById("lead-capture")?.scrollIntoView({ behavior: "smooth", block: "start" });
  const handleFix = async (item) => {
    const key = item.code || item.problem || item.title;
    if (added[key]) return;
    const pmap = { high: "High", medium: "Medium", opportunity: "Low" };
    try {
      const { data } = await api.post("/tasks", {
        scan_id: id,
        business_key: scan.business_key,
        business_name: scan.business?.name,
        code: item.code || null,
        problem: item.problem || item.title,
        why_it_matters: item.why_it_matters,
        recommended_action: item.recommended_action,
        impact_note: item.impact_note,
        priority: pmap[item.priority] || item.priority || "Medium",
        effort: item.effort,
        confidence: item.confidence,
        evidence: item.evidence,
        estimated_value: item.estimated_value ?? null,
        urgency: item.urgency || null,
        impact_type: item.impact_type || item.financial?.impact_type || null,
        impact_monthly: item.impact_monthly || item.financial?.monthly || null,
        impact_annual: item.impact_annual || item.financial?.annual || null,
      });
      setAdded((a) => ({ ...a, [key]: true }));
      refreshFixStats();
      if (data.created) {
        toast.success("Added to your Fix Plan", { action: { label: "View", onClick: () => nav("/tasks") } });
      } else {
        toast.message("Already in your Fix Plan", { action: { label: "View", onClick: () => nav("/tasks") } });
      }
    } catch (e) {
      toast.error("Could not add to Fix Plan. Please try again.");
    }
  };
  const handleFinancialFix = async (issue) => {
    const fmap = { "QUICK WIN": "High", "HIGH PRIORITY": "High", "MEDIUM PRIORITY": "Medium", "LONG-TERM": "Medium", "LOW PRIORITY": "Low" };
    const sym = scan?.report?.financial?.currency_symbol || "$";
    const annualMid = issue.adjusted_annual ? Math.round((issue.adjusted_annual[0] + issue.adjusted_annual[1]) / 2) : null;
    const impact = issue.adjusted_annual
      ? `Estimated ${sym}${issue.adjusted_annual[0].toLocaleString()}–${sym}${issue.adjusted_annual[1].toLocaleString()}/yr (modeled)`
      : issue.impact_note;
    await handleFix({
      code: issue.code, problem: issue.problem, why_it_matters: issue.why_it_matters,
      recommended_action: issue.recommended_action, evidence: issue.evidence,
      effort: issue.effort, confidence: issue.confidence,
      priority: fmap[issue.priority_category] || "Medium",
      impact_note: impact, estimated_value: annualMid,
      urgency: issue.urgency, impact_type: issue.impact_type,
      impact_monthly: issue.gross_monthly, impact_annual: issue.gross_annual,
    });
  };
  const handleGetFixed = () => { setLeadPrefill({ interest: "Full business audit", message: "" }); scrollToLead(); };
  const handleBookReview = () => { setLeadPrefill({ interest: "Book a business review", message: "" }); scrollToLead(); };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/scan/${id}`);
        if (!active) return;
        setScan(data);
        setStatus(data.status);
        if (data.status === "complete") saveScanId(id);
        if (data.status !== "complete" && data.status !== "failed") setTimeout(load, 1500);
      } catch (e) {
        if (active) setStatus("notfound");
      }
    };
    load();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    if (status !== "complete" || !scan?.business_key) return;
    setBizSaved(getSavedKeys().includes(scan.business_key));
    let active = true;
    (async () => {
      try {
        const [{ data: listData }, { data: statsData }] = await Promise.all([
          api.post("/tasks/list", { business_keys: [scan.business_key] }),
          api.post("/tasks/stats", { business_keys: [scan.business_key] }),
        ]);
        if (!active) return;
        const map = {};
        (listData.tasks || []).forEach((t) => {
          if (t.status === "DISMISSED") return;
          if (t.code) map[t.code] = true;
          if (t.problem) map[t.problem] = true;
        });
        setAdded((a) => ({ ...map, ...a }));
        setFixStats(statsData);
      } catch (e) { /* ignore */ }
    })();
    return () => { active = false; };
  }, [status, scan?.business_key]);

  const refreshFixStats = async () => {
    if (!scan?.business_key) return;
    try {
      const { data } = await api.post("/tasks/stats", { business_keys: [scan.business_key] });
      setFixStats(data);
    } catch (e) { /* ignore */ }
  };

  const share = async () => {
    const url = window.location.href;
    try { await navigator.clipboard.writeText(url); toast.success("Report link copied to clipboard"); }
    catch { toast.message(url); }
  };

  if (status === "loading" || status === "pending" || status === "running") {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="font-heading text-lg font-semibold text-slate-900">Preparing your report…</p>
          <p className="text-sm text-slate-500 mt-1">This usually takes under a minute.</p>
        </div>
      </div>
    );
  }
  if (status === "notfound" || status === "failed") {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-10 h-10 text-orange-500 mx-auto mb-4" />
          <p className="font-heading text-xl font-semibold text-slate-900">
            {status === "failed" ? "This scan could not be completed" : "Report not found"}
          </p>
          <p className="text-sm text-slate-500 mt-2">
            {scan?.error || "The website may be unreachable or the link is invalid. Try running a new scan."}
          </p>
          <Button onClick={() => nav("/scan")} className="mt-6 rounded-lg bg-slate-900 hover:bg-slate-800 text-white">Run a new scan</Button>
        </div>
      </div>
    );
  }

  const r = scan.report || {};
  const biz = scan.business || {};
  const counts = r.counts || {};
  const highCount = (counts.critical || 0) + (counts.high || 0);

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Action bar */}
      <div className="sticky top-16 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-heading font-bold text-slate-900 truncate">{biz.name}</p>
            <p className="text-xs text-slate-500 truncate">{biz.category} · {biz.city}, {biz.state} · {new Date(scan.created_at).toLocaleDateString()}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {r.business_profile && (
              <button onClick={saveToMyBusinesses} data-testid="report-save-business" disabled={savingBiz || bizSaved}
                className={`inline-flex items-center gap-1.5 rounded-lg border text-sm font-medium px-4 py-2 transition-colors ${bizSaved ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 hover:border-indigo-300 text-slate-700 hover:text-indigo-700"}`}>
                {bizSaved ? <BookmarkCheck className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                <span className="hidden sm:inline">{bizSaved ? "Saved" : "Save business"}</span>
              </button>
            )}
            <a href={`${API}/scan/${id}/pdf`} data-testid="download-pdf"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-4 py-2 transition-colors active:scale-95">
              <Download className="w-4 h-4" /> <span className="hidden sm:inline">PDF</span>
            </a>
            <button onClick={share} data-testid="share-report"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-700 text-sm font-medium px-4 py-2 transition-colors">
              <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">Share</span>
            </button>
            <button onClick={() => nav("/scan")} data-testid="new-scan"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-700 text-sm font-medium px-4 py-2 transition-colors">
              <RotateCcw className="w-4 h-4" /> <span className="hidden sm:inline">New</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 space-y-10">
        {/* Hero / score */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 border border-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Business Opportunity Report
          </div>
          <div className="grid lg:grid-cols-12 gap-8 items-center bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 shadow-sm">
            <div className="lg:col-span-4 flex flex-col items-center">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 mb-4">Business Health Score</p>
              <HealthGauge score={r.health_score} />
              <p className="mt-3 text-[11px] text-slate-500 text-center max-w-[220px]">{r.score_label}</p>
              {r.scan_mode === "bi" && r.compliance?.available && (
                <div className="mt-5 w-full"><ComplianceScoreBadge compliance={r.compliance} testid="compliance-score-header" /></div>
              )}
            </div>
            <div className="lg:col-span-8">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-snug">{r.summary_headline}</h1>
              <div className="mt-5 grid grid-cols-3 gap-3">
                <Stat value={counts.total ?? 0} label="Opportunities found" />
                <Stat value={highCount} label="High priority" accent="text-red-600" />
                <Stat value={r.categories?.filter((c) => c.score != null && c.score < 60).length ?? 0} label="Categories to improve" accent="text-orange-600" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Executive summary + provenance legend */}
        <section className="space-y-6">
          {r.business_profile && <BusinessProfileCard profile={r.business_profile} />}
          <ExecutiveSummary summary={r.executive_summary} version={r.report_version} overview={r.business_overview} date={new Date(scan.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })} />
          <ProvenanceLegend />
        </section>

        {/* Total potential impact — three SEPARATE categories (never combined) */}
        {r.scan_mode === "bi" && (r.financial || r.compliance?.available) && (
          <section>
            <SectionTitle eyebrow="Total potential impact" title="Opportunity, Exposure & Remediation" icon={Sparkles} />
            <ImpactOverview financial={r.financial} compliance={r.compliance} sym={r.financial?.currency_symbol || "$"} />
          </section>
        )}

        {/* Top opportunities */}
        <section>
          <SectionTitle eyebrow="What to fix first" title="Top Business Opportunities" icon={Sparkles} />
          <TopOpportunities items={r.top_opportunities} onFix={handleFix} added={added} />
        </section>

        {/* Solutions & pricing CTA — turn findings into a costed, buyable plan */}
        <section>
          <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            data-testid="solutions-cta">
            <div className="min-w-0">
              <p className="text-indigo-600 font-semibold text-xs uppercase tracking-[0.2em]">Ready to act?</p>
              <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mt-1">See how we can fix this — with pricing</h3>
              <p className="text-sm text-slate-600 mt-1.5 max-w-xl">Turn each finding into a costed solution and build a professional proposal you can pay online. Prices are AI-assisted estimates, never guarantees.</p>
            </div>
            <Button onClick={() => nav(`/scan/${id}/solutions`)} data-testid="view-solutions-btn"
              className="shrink-0 bg-slate-900 hover:bg-slate-800 active:scale-95 transition-transform">
              <Wrench className="w-4 h-4 mr-2" /> Solutions &amp; Pricing
            </Button>
          </div>
          <button onClick={() => nav(`/scan/${id}/financials`)} data-testid="financials-cta"
            className="mt-3 w-full text-left rounded-xl border border-slate-200 bg-white hover:border-slate-300 p-4 flex items-center justify-between gap-3 transition-colors">
            <span className="min-w-0">
              <span className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-400" /> Private financial analysis
              </span>
              <span className="block text-xs text-slate-500 mt-0.5">Owner tool — enter your own sales &amp; costs to see margins, break-even and target progress. Only you can see this; it never appears on the report or any shared link.</span>
            </span>
            <span className="shrink-0 text-sm font-medium text-indigo-600">Open →</span>
          </button>
        </section>

        {/* Social Media Intelligence (all scan modes, when computed) */}
        {r.social_intelligence?.enabled && (
          <section>
            <SectionTitle eyebrow="Social presence" title="Social Media Intelligence" icon={Sparkles} />
            <SocialIntelligence social={r.social_intelligence} />
          </section>
        )}

        {/* Owner's Business Concerns (all scan modes, when the owner shared any) */}
        {r.owner_concerns?.enabled && (
          <section>
            <SectionTitle eyebrow="Your perspective" title="What's Holding You Back" icon={Sparkles} />
            <OwnerConcerns data={r.owner_concerns} />
          </section>
        )}

        {/* Industry Source Registry (all scan modes) */}
        {r.industry_sources?.enabled && (
          <section>
            <SectionTitle eyebrow="Where customers look" title="Industry Listings & Review Sources" icon={Sparkles} />
            <IndustrySources data={r.industry_sources} />
          </section>
        )}

        {/* Reputation Intelligence (all scan modes): verified ratings + listings + BBB + health score */}
        {r.reputation_intelligence?.enabled && (
          <section>
            <SectionTitle eyebrow="Trust & reputation" title="Reputation Intelligence" icon={Sparkles} />
            <ReputationIntelligence data={r.reputation_intelligence} business={biz} onFix={handleFix} added={added} />
          </section>
        )}

        {/* Financial Impact / Potential Loss Engine (BI scans only) */}
        {r.scan_mode === "bi" && r.financial && (
          <section>
            <SectionTitle eyebrow="Estimated · Modeled" title="Financial Opportunity Analysis" icon={Sparkles} />
            <FinancialImpact financial={r.financial} sym={r.financial.currency_symbol || "$"} onAddToPlan={handleFinancialFix} added={added}
              admin={isAdmin} onRecompute={recomputeFinancial} onOverride={overrideFinancial} version={finVersion ?? scan.financial_version} />
          </section>
        )}

        {/* Licenses & Compliance (BI scans only; honest, evidence-first) */}
        {r.scan_mode === "bi" && r.compliance?.available && (
          <section>
            <SectionTitle eyebrow="Regulatory · Evidence-first" title="License & Compliance" icon={Sparkles} />
            <ComplianceSection compliance={r.compliance} onFix={handleFix} added={added} admin={isAdmin} onOverride={overrideCompliance} />
          </section>
        )}

        {/* 360° Business Intelligence dashboard (BI scans only) */}
        {r.scan_mode === "bi" && (
          <section>
            <SectionTitle eyebrow="360° discovery" title="Business Intelligence" icon={Sparkles} />
            <BusinessIntelDashboard report={r} scanId={id} />
          </section>
        )}

        {/* Bento: category scores + reputation + outdated + menu */}
        <section className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 bg-white rounded-xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-1">Category Breakdown</h3>
            <p className="text-sm text-slate-500 mb-5">How your business scores across five areas.</p>
            <CategoryScores categories={r.categories || []} />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6 auto-rows-min">
            <ReputationCard reputation={r.reputation} />
            <OutdatedInfoCard items={r.outdated_info} />
            <div className="sm:col-span-2"><MenuServicesCard menu={r.menu_services} /></div>
          </div>
        </section>

        {/* Detailed findings */}
        <section>
          <SectionTitle eyebrow="Website & business audit" title="Detailed Findings" />
          <FindingsSection findings={r.findings || []} onFix={handleFix} added={added} />
        </section>

        {/* Competitors */}
        <section><CompetitorSection competitors={r.competitors} /></section>

        {/* Side-by-Side Battlecard (renders only when there is a competitor set) */}
        <section><Battlecard report={r} business={biz} scanId={id} /></section>

        {/* Potential opportunity */}
        <section><PotentialOpportunity potential={r.potential_opportunity} /></section>

        {/* Fix plan summary (if the user has tracked opportunities) */}
        {fixStats?.tracked ? (
          <section><FixPlanSummary stats={fixStats} onView={() => nav("/tasks")} /></section>
        ) : null}

        {/* Clear next steps */}
        <section><NextSteps steps={r.next_steps} /></section>

        {/* Lead gen */}
        <section><LeadGenCTA onRescan={() => nav("/scan")} onGetFixed={handleGetFixed} onBookReview={handleBookReview} /></section>

        {/* Lead capture form */}
        <section>
          <LeadForm scanId={id} businessName={biz.name} healthScore={r.health_score} prefill={leadPrefill} />
        </section>

        {/* Disclaimer */}
        <p className="text-xs text-slate-400 leading-relaxed border-t border-slate-200 pt-6">{r.disclaimer}</p>
      </div>
    </div>
  );
}

function Stat({ value, label, accent = "text-slate-900" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className={`font-heading text-3xl font-bold leading-none ${accent}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1.5">{label}</div>
    </div>
  );
}
